/**
 * apps/battle/client/src/terminal/screens/EntryNarrativeScreen.tsx
 * Cluster entry narrative. Auto-advances after 8 s or on [Enter].
 */
import { useEffect, useState } from 'react';
import { TerminalFrame } from '../components/TerminalFrame.js';
import { KeyBar, type KeyBarItem } from '../components/KeyBar.js';
import { useKeyboard } from '../hooks/useKeyboard.js';
import { CLUSTER_NARRATIVE, type ClusterId } from '../narrative.js';
import { NARRATIVE_AUTO_ADVANCE_MS } from '../config.js';
import type { ClusterState } from '../../api.js';

interface Props {
  cluster: ClusterState;
  onBegin: () => void;
  onBackToTitle: () => void;
}

export function EntryNarrativeScreen({ cluster, onBegin, onBackToTitle }: Props) {
  const id = cluster.clusterId as ClusterId;
  const n = CLUSTER_NARRATIVE[id];
  const [countdown, setCountdown] = useState(Math.round(NARRATIVE_AUTO_ADVANCE_MS / 1000));

  useEffect(() => {
    const t = setTimeout(onBegin, NARRATIVE_AUTO_ADVANCE_MS);
    const i = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => { clearTimeout(t); clearInterval(i); };
  }, [onBegin]);

  useKeyboard([
    { key: 'Enter', handler: onBegin },
    { key: 'b', handler: onBackToTitle },
  ]);

  const title = (
    <>
      ECONQUEST <span className="tm-text--dim">{'\u2500'}</span> CLUSTER {cluster.clusterId}{' '}
      <span className="tm-text--dim">{'\u2500'}</span>{' '}
      <span className="tm-text--accent">ENTERING</span>
    </>
  );

  // Use DISTINCT concept counts (matches graph-mode 7/11), not raw regularBattlesTotal.
  const total = cluster.conceptTotal;
  const cleared = cluster.conceptCleared;
  const remaining = Math.max(0, total - cleared);

  const keybar: KeyBarItem[] = [
    { label: 'Enter', description: 'begin', onActivate: onBegin },
    { label: 'B', description: 'back to title', onActivate: onBackToTitle },
  ];

  // Build a natural progress line:
  //  - fresh cluster:     "21 battles await."
  //  - partial progress:  "21 battles · 10 cleared · 11 remain."
  //  - fully cleared:     "All 21 battles cleared."
  let progressLine: string | null = null;
  if (total > 0) {
    const battleWord = `battle${total === 1 ? '' : 's'}`;
    if (cleared === 0) {
      progressLine = `${total} ${battleWord} await.`;
    } else if (cleared >= total) {
      progressLine = `All ${total} ${battleWord} cleared.`;
    } else {
      progressLine = `${total} ${battleWord} \u00B7 ${cleared} cleared \u00B7 ${remaining} remain.`;
    }
  }

  return (
    <TerminalFrame title={title} keybar={<KeyBar items={keybar} />}>
      <div className="tm-narrative">
        <div className="tm-h1">{n?.name ?? cluster.fullName}</div>
        {n?.entry ?? `You enter ${cluster.fullName}.`}
        {progressLine && (
          <div className="tm-narrative__sub">{progressLine}</div>
        )}
        <div className="tm-narrative__sub">
          {'>'} auto-advance in {countdown}s
        </div>
      </div>
    </TerminalFrame>
  );
}
